/**
 * Temporary debug instrumentation for CRM stage-move lifecycle.
 * Enable: ?crm_debug=1 in URL, or localStorage.setItem('crm_debug', '1')
 * Filter console: [CRM-DEBUG]
 * Tracks last moved deal: set when drag-drop starts, logs only for that deal.
 */
const TRACK_KEY = "crm_debug_track";

const enabled = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.location.search.includes("crm_debug=1") ||
    localStorage.getItem("crm_debug") === "1"
  );
};

export function setTrackedDealId(dealId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(TRACK_KEY, dealId);
  } catch {
    /* ignore */
  }
}

export function getTrackedDealId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(TRACK_KEY);
  } catch {
    return null;
  }
}

/** Headers to add to API requests when debugging (backend can log for this deal). */
export function getDebugRequestHeaders(): Record<string, string> {
  const id = getTrackedDealId();
  if (!id || !enabled()) return {};
  return { "X-Debug-Track-Deal": id };
}

function shouldLog(dealId: string | undefined): boolean {
  if (!enabled() || !dealId) return false;
  const tracked = getTrackedDealId();
  return !tracked || tracked === dealId;
}

export function crmDebug(
  step: string,
  dealId: string,
  data: {
    stageId?: string | null;
    source: string;
    pipelineId?: string | null;
    contactId?: string | null;
    [k: string]: unknown;
  }
): void {
  if (!shouldLog(dealId)) return;
  console.log("[CRM-DEBUG]", step, dealId, JSON.stringify(data));
}

export function crmDebugDeal(
  step: string,
  deal: { id?: string; stageId?: string; stage_id?: string; pipelineId?: string; pipeline_id?: string; contactId?: string; contact_id?: string } | null | undefined,
  source: string
): void {
  if (!deal) return;
  const id = (deal as { id?: string }).id ?? "?";
  if (!shouldLog(id)) return;
  const d = deal as Record<string, unknown>;
  crmDebug(step, id, {
    stageId: (d.stageId ?? d.stage_id ?? null) as string | null,
    pipelineId: (d.pipelineId ?? d.pipeline_id ?? null) as string | null,
    contactId: (d.contactId ?? d.contact_id ?? null) as string | null,
    source,
  });
}

/** Navigation / mount debug – always logs when enabled (no deal filter). */
export function crmDebugNav(step: string, data: Record<string, unknown>): void {
  if (!enabled()) return;
  console.log("[CRM-DEBUG]", step, JSON.stringify(data));
}
